import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import axios from 'axios';
import Swal from 'sweetalert2';
import {
    ChevronLeft,
    ChevronRight,
    Clock,
    Video,
    CheckCircle2,
    AlertTriangle,
    Loader2
} from 'lucide-react';
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    startOfWeek,
    endOfWeek,
    isSameMonth,
    isToday,
    addMonths,
    subMonths,
    isSameDay,
    isBefore,
    startOfDay,
    addHours,
    isAfter,
    addDays
} from 'date-fns';
import './Counselling.css';
import ConfirmationModal from './ConfirmationModal';

// Environment variables for API calls
const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
const REACT_APP_LOGIN_APP_API_BASE = process.env.REACT_APP_LOGIN_APP_API_BASE;
const REACT_APP_COUNSELLING_API_BASE = process.env.REACT_APP_COUNSELLING_API_BASE;

// Helper function for styled alerts
const showStyledAlert = (options) => {
    const SwalStyled = Swal.mixin({
        customClass: {
            popup: 'custom-swal-popup',
            title: 'swal2-title',
            htmlContainer: 'swal2-html-container',
            confirmButton: 'swal2-confirm',
            cancelButton: 'swal2-cancel'
        },
        buttonsStyling: false,
    });
    SwalStyled.fire({
        showClass: { popup: 'animate__animated animate__fadeInUp animate__faster' },
        hideClass: { popup: 'animate__animated animate__fadeOutDown animate__faster' },
        ...options
    });
};

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

const generateTimeSlots = (startHour, endHour, intervalMinutes) => {
    const slots = [];
    const startTime = new Date();
    startTime.setHours(startHour, 0, 0, 0);
    const endTime = new Date();
    endTime.setHours(endHour, 0, 0, 0);
    let currentTime = new Date(startTime);
    while (currentTime < endTime) {
        slots.push(format(currentTime, 'h:mmaa').toLowerCase());
        currentTime.setMinutes(currentTime.getMinutes() + intervalMinutes);
    }
    return slots;
};

const Counselling = ({ onBookingSuccess, isInsideModal = false }) => {
    const query = useQuery();
    const rescheduleId = query.get('rescheduleId');
    const navigate = useNavigate();

    
   const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

    // --- State Management ---
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [userError, setUserError] = useState('');
    const [isBooking, setIsBooking] = useState(false);
    const [bookingError, setBookingError] = useState('');
    const [bookingSuccessMessage, setBookingSuccessMessage] = useState('');

    // --- Coupon & Price State ---
    const [couponError, setCouponError] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [priceDetails, setPriceDetails] = useState({
        original: 499,
        discount: 0,
        gst: 499 * 0.18,
        final: 499 + (499 * 0.18)
    });

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);

    const todayStart = startOfDay(new Date());
    const maxSelectableDate = addDays(todayStart, 15);

    useEffect(() => {
        const basePrice = 499;
        const gst = basePrice * 0.18;
        setPriceDetails({
            original: basePrice, discount: 0, gst: gst, final: basePrice + gst
        });
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            setLoadingUser(true);
            setUserError('');
            try {
                const userId = localStorage.getItem('userId');
                if (!userId) {
                    throw new Error("You must be logged in to book a session.");
                }
                const response = await axios.get(`${REACT_APP_LOGIN_APP_API_BASE}/api/user/${userId}`);
                setUserData(response.data);
            } catch (err) {
                setUserError(err.response?.data?.message || err.message || 'Could not fetch your details.');
            } finally {
                setLoadingUser(false);
            }
        };
        fetchUserData();
    }, []);

    const allAvailableTimes = useMemo(() => generateTimeSlots(9, 18, 30), []);

    const filteredTimeSlots = useMemo(() => {
        if (!selectedDate) return [];
        if (!isSameDay(selectedDate, new Date())) return allAvailableTimes;
        const minimumBookingTime = addHours(new Date(), 2);
        return allAvailableTimes.filter(timeSlot => {
            let [hours, minutes] = timeSlot.slice(0, -2).split(':').map(Number);
            if (timeSlot.endsWith('pm') && hours !== 12) hours += 12;
            if (timeSlot.endsWith('am') && hours === 12) hours = 0;
            const slotDateTime = new Date(selectedDate);
            slotDateTime.setHours(hours, minutes, 0, 0);
            return isAfter(slotDateTime, minimumBookingTime);
        });
    }, [selectedDate, allAvailableTimes]);


    // In Counselling.js

    const handleReschedule = useCallback(async () => {
        setIsBooking(true);
        setBookingError('');
        try {
            const payload = {
                date: format(selectedDate, 'yyyy-MM-dd'),
                time: selectedTime
            };
 
            // This axios call correctly targets your gateway server on port 5000.
            // The gateway will then handle forwarding it to port 8080.
            const resp = await axios.put(
                `${REACT_APP_API_BASE}/api/counselling/reschedule/${rescheduleId}`,
                payload
            );

            setBookingSuccessMessage(resp.data.message || 'Your session has been rescheduled!');
        } catch (err) {
            setBookingError(
                err.response?.data?.message || 'Could not reschedule your session.'
            );
        } finally {
            setIsBooking(false);
        }
    }, [rescheduleId, selectedDate, selectedTime, REACT_APP_API_BASE]);


    const handleRemoveCoupon = useCallback(() => {
        setAppliedCoupon(null);
        setCouponError('');
        const basePrice = 499;
        const gst = basePrice * 0.18;
        setPriceDetails({ original: basePrice, discount: 0, gst: gst, final: basePrice + gst });
    }, []);

 const handleApplyCoupon = useCallback(async (codeToApply) => {
    const trimCode = (codeToApply || '').trim();
    if (!trimCode) {
        setCouponError("Please enter a coupon code.");
        return;
    }

    // helper to get userId
    const getUserIdFromStorage = () => {
        const direct = localStorage.getItem('userId');
        if (direct) return direct;
        try {
            const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
            return cu?.userId || cu?._id || cu?.id || null;
        } catch (e) {
            return null;
        }
    };

    const userId = getUserIdFromStorage();
    if (!userId) {
        const msg = 'You must be logged in to apply a coupon.';
        setCouponError(msg);
        showStyledAlert({ icon: 'error', title: 'Coupon Error', text: msg });
        return;
    }

    setCouponLoading(true);
    setCouponError('');
    try {
        // include token if available (optional)
        const token = localStorage.getItem('token') || (() => {
            try {
                const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
                return cu?.token || '';
            } catch (e) {
                return '';
            }
        })();

        const payload = {
            code: trimCode,
            serviceType: 'counselling',
            userId, // send userId as backend expects
        };

        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

        const response = await axios.post(
            `${REACT_APP_COUNSELLING_API_BASE}/api/coupons/validate`,
            payload,
            config
        );

        const coupon = response.data;
        setAppliedCoupon(coupon);

        const basePrice = 499;
        let discount = 0;
        if (coupon.discountType === 'percentage') {
            discount = (basePrice * coupon.discountValue) / 100;
        } else if (coupon.discountType === 'fixed') {
            discount = coupon.discountValue;
        }

        const discountedPrice = Math.max(0, basePrice - discount);
        const gst = Number((discountedPrice * 0.18).toFixed(2));
        const finalPrice = Number((discountedPrice + gst).toFixed(2));

        setPriceDetails({ original: basePrice, discount, gst, final: finalPrice });
        setCouponError('');
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Could not apply coupon.";
        setCouponError(errorMessage);
        showStyledAlert({ icon: 'error', title: 'Coupon Error', text: errorMessage });
        handleRemoveCoupon();
    } finally {
        setCouponLoading(false);
    }
}, [handleRemoveCoupon, REACT_APP_COUNSELLING_API_BASE]);


    const initiatePayment = async () => {
        setIsBooking(true);
        setBookingError('');
        const userId = localStorage.getItem('userId');
        const orderPayload = {
            userId,
            date: format(selectedDate, 'yyyy-MM-dd'),
            time: selectedTime,
            couponCode: appliedCoupon ? appliedCoupon.code : null,
        };

        if (priceDetails.final <= 0) {
            try {
                const response = await axios.post(`${REACT_APP_API_BASE}/api/payment/create-order`, orderPayload);
                setBookingSuccessMessage(response.data.message || 'Your booking is confirmed!');
                setIsModalOpen(false);
                setIsBooking(false);
            } catch (error) {
                setBookingError(error.response?.data?.message || 'Could not process your free booking.');
                setIsBooking(false);
            }
        } else {
            try {
                const orderResponse = await axios.post(`${REACT_APP_API_BASE}/api/payment/create-order`, orderPayload);
                const { key, orderId, amount, currency } = orderResponse.data;

                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY,
                    amount, currency,
                    name: "Get Next Visa",
                    description: "1-on-1 Counselling Session",
                    image: "/photos/newlogo.png",

                    order_id: orderId,
                    handler: async (response) => {
                        setIsBooking(true);
                        try {
                            const verificationPayload = { ...response, userId, date: format(selectedDate, 'yyyy-MM-dd'), time: selectedTime };
                            const verificationResponse = await axios.post(`${REACT_APP_API_BASE}/api/payment/verify`, verificationPayload);
                            setBookingSuccessMessage(verificationResponse.data.message || 'Your booking is confirmed!');
                            setIsModalOpen(false);
                        } catch (error) {
                            setBookingError(error.response?.data?.message || 'Payment verification failed.');
                            setIsBooking(false);
                        }
                    },
                    prefill: { name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(), email: userData.email, contact: userData.phone },
                    theme: { color: "#003366" },
                    modal: { ondismiss: () => { if (!bookingSuccessMessage) { setBookingError('Payment was cancelled.'); setIsBooking(false); } } }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            } catch (error) {
                setBookingError(error.response?.data?.message || 'Could not initiate payment.');
                setIsBooking(false);
            }
        }
    };

    const handleDateSelect = (day) => { setSelectedDate(day); setSelectedTime(null); setBookingError(''); };
    const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const handleSuccessAction = () => {
        if (onBookingSuccess) onBookingSuccess();
        if (!isInsideModal) navigate('/dashboard?view=sessions');
    };

    const renderCalendar = () => {
        return (
            <div className="calendar-container">
                <div className="calendar-header">
                    <button onClick={prevMonth} className="nav-button"><ChevronLeft size={20} /></button>
                    <span className="current-month">{format(currentMonth, 'MMMM yyyy')}</span>
                    <button onClick={nextMonth} className="nav-button"><ChevronRight size={20} /></button>
                </div>
                <div className="calendar-grid weekdays">
                    {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(d => <div key={d} className="weekday">{d}</div>)}
                </div>
                <div className="calendar-grid days">
                    {eachDayOfInterval({
                        start: startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }),
                        end: endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 })
                    }).map(day => {
                        const isPast = isBefore(day, todayStart);
                        const isTooFar = isAfter(day, maxSelectableDate);
                        const isOtherMonth = !isSameMonth(day, currentMonth);
                        const disabled = isPast || isTooFar || isOtherMonth;
                        const isSelected = selectedDate && isSameDay(day, selectedDate);

                        return (
                            <button
                                key={day.toISOString()}
                                className={[
                                    'day-cell-counselling',
                                    isOtherMonth && 'other-month',
                                    isToday(day) && 'today',
                                    isSelected && 'selected',
                                    disabled && 'disabled-day'
                                ].filter(Boolean).join(' ')}
                                onClick={() => !disabled && handleDateSelect(day)}
                                disabled={disabled}
                            >
                                {format(day, 'd')}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderTimeSlots = () => (
        <div className="timeslot-container">
            <div className="timeslot-header">
                <h3>{format(selectedDate, 'EEEE, MMMM d')}</h3>
                <button className="back-to-calendar" onClick={() => setSelectedDate(null)}><ChevronLeft size={16} /> Back to Calendar</button>
            </div>
            <div className="timeslot-grid">
                {filteredTimeSlots.length > 0 ? (
                    filteredTimeSlots.map(time => <button key={time} className={`time-slot ${selectedTime === time ? 'selected' : ''}`} onClick={() => setSelectedTime(time)} disabled={isBooking}>{time}</button>)
                ) : (
                    <div className="no-slots-message">No available slots for today. Please select another date.</div>
                )}
            </div>
            {selectedTime && (
                <button
                    className="confirm-booking-btn"
                    onClick={rescheduleId ? handleReschedule : () => setIsModalOpen(true)}
                    disabled={isBooking || loadingUser}>
                    {isBooking && rescheduleId ? (
                        <><Loader2 className="animate-spin" size={20} /> Rescheduling...</>
                    ) : rescheduleId ? (
                        `Confirm Reschedule for ${selectedTime}`
                    ) : (
                        `Proceed to Pay ₹${Number(priceDetails.final).toFixed(2)}`
                    )}
                </button>
            )}
            {bookingError && !isModalOpen && <div className="booking-message error"><AlertTriangle size={18} /><span>{bookingError}</span></div>}
        </div>
    );

    const renderSuccessView = () => (
        <div className="booking-success-container">
            <div className="success-animation-container">
                <div className="success-checkmark-bg"></div>
                <CheckCircle2 size={52} className="success-checkmark-icon" />
            </div>
            <h2>{rescheduleId ? 'Session Rescheduled!' : 'Booking Confirmed!'}</h2>
            <p className="success-subtitle">Your 1-on-1 counselling session is locked in. We've sent all the details to your email.</p>
            <div className="success-details-box">
                <div className="success-detail-item"><span className="detail-label">Date:</span><span className="detail-value">{format(selectedDate, 'EEEE, dd MMMM yyyy')}</span></div>
                <div className="success-detail-item"><span className="detail-label">Time:</span><span className="detail-value">{selectedTime}</span></div>
                <div className="success-detail-item"><span className="detail-label">Via:</span><span className="detail-value">Zoom Meet (Link in email)</span></div>
            </div>
            <div className="success-actions">
                <button onClick={handleSuccessAction} className="success-action-btn primary">{isInsideModal ? 'Done' : 'Go to My Bookings'}</button>
            </div>
        </div>
    );

    const renderBookingInfo = () => {
        if (loadingUser) return (<div className="info-loading-state"> <Loader2 className="animate-spin" size={24} /> <p>Loading your info...</p> </div>);
        if (userError) return <div className="info-error-state">{userError}</div>;
        if (userData) {
            const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
            return (
                <>
                    <div className="profile-icon">{userData.avatarUrl ? <img src={userData.avatarUrl} alt="Profile" className="profile-avatar" /> : (fullName ? fullName.charAt(0).toUpperCase() : 'U')}</div>
                    <h3 className="counsellor-name">{fullName || "1-on-1 Counselling"}</h3>
                    <h1 className="meeting-title">{rescheduleId ? 'Reschedule Your Session' : 'Let\'s Connect'}</h1>
                    <div className="info-item"> <Clock size={18} /> <span>30 min</span> </div>
                    <div className="info-item"> <Video size={18} /> <span>Web conferencing details provided upon confirmation.</span> </div>
                    <div className="info-how-it-works">
                        <h2>How It Works</h2>
                        <div className="step"><div className="step-icon-container selected"> <div className="counselling-icons">🗓️</div> </div><div> <h3>Select a Date & Time</h3> <p>Choose any available day from the calendar.</p> </div></div>
                        <div className="step"><div className="step-icon-container"> <div className="counselling-icons">🕒</div> </div><div> <h3>Pick a Time</h3> <p>Browse and select a convenient time slot.</p> </div></div>
                        <div className="step"><div className="step-icon-container"> <div className="counselling-icons">✔️</div> </div><div> <h3>Confirm Your Session</h3> <p>Finalize your booking and get ready to connect.</p> </div></div>
                    </div>
                </>
            );
        }
        return null;
    };

    const Wrapper = isInsideModal ? 'div' : ({ children }) => <div className="counselling-page-container">{children}</div>;

    return (
        <Wrapper>

            <Helmet>
                <meta name="title" content="Home | Get Next Visa - AI Powered Student Visa Report" />
                <title>Counselling | Get Next Visa - AI Powered Student Visa Report</title>
                <meta name="description" content="Get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
            </Helmet>

            <div className="booking-card">
                {/* Section 1: User Info (The top part of the left panel) */}
                <div className="booking-info">
                    {loadingUser ? (
                        <div className="info-loading-state"> <Loader2 className="animate-spin" size={24} /> <p>Loading your info...</p> </div>
                    ) : userError ? (
                        <div className="info-error-state">{userError}</div>
                    ) : userData && (
                        <>
                            <div className="profile-icon">{userData.avatarUrl ? <img src={userData.avatarUrl} alt="Profile" className="profile-avatar" /> : (`${userData.firstName || ''} ${userData.lastName || ''}`.trim().charAt(0).toUpperCase() || 'U')}</div>
                            <h3 className="counsellor-name">{`${userData.firstName || ''} ${userData.lastName || ''}`.trim() || "1-on-1 Counselling"}</h3>
                            <h1 className="meeting-title">{rescheduleId ? 'Reschedule Your Session' : 'Let\'s Connect'}</h1>
                            <div className="info-item"> <Clock size={18} /> <span>30 min</span> </div>
                            <div className="info-item"> <Video size={18} /> <span>Web conferencing details provided upon confirmation.</span> </div>

                            {/* The original "How It Works" section, which will now ONLY show on desktop */}
                            <div className="info-how-it-works desktop-only">
                                <h2>How It Works</h2>
                                <div className="step"><div className="step-icon-container selected"> <div className="counselling-icons">🗓️</div> </div><div> <h3>Select a Date & Time</h3> <p>Choose any available day from the calendar.</p> </div></div>
                                <div className="step"><div className="step-icon-container"> <div className="counselling-icons">🕒</div> </div><div> <h3>Pick a Time</h3> <p>Browse and select a convenient time slot.</p> </div></div>
                                <div className="step"><div className="step-icon-container"> <div className="counselling-icons">✔️</div> </div><div> <h3>Confirm Your Session</h3> <p>Finalize your booking and get ready to connect.</p> </div></div>
                            </div>
                        </>
                    )}
                </div>

                {/* Section 2: The Scheduler (Calendar/Time Slots) */}
                <div className="booking-scheduler">
                    {bookingSuccessMessage ? (
                        renderSuccessView()
                    ) : !selectedDate ? (
                        <>
                            <div className="calendar-logo-container">
                                <img src="/photos/heading.png" alt="Get Next Visa Logo" className="calendar-logo" />
                            </div>
                            {renderCalendar()}
                        </>
                    ) : (
                        renderTimeSlots()
                    )}
                </div>

                {/* Section 3: The new "How It Works" section, which will ONLY show on mobile */}
                <div className="info-how-it-works mobile-only">
                    <h2>How It Works</h2>
                    <div className="step"><div className="step-icon-container selected"> <div className="counselling-icons">🗓️</div> </div><div> <h3>Select a Date & Time</h3> <p>Choose any available day from the calendar.</p> </div></div>
                    <div className="step"><div className="step-icon-container"> <div className="counselling-icons">🕒</div> </div><div> <h3>Pick a Time</h3> <p>Browse and select a convenient time slot.</p> </div></div>
                    <div className="step"><div className="step-icon-container"> <div className="counselling-icons">✔️</div> </div><div> <h3>Confirm Your Session</h3> <p>Finalize your booking and get ready to connect.</p> </div></div>
                </div>
            </div>

            {!rescheduleId && (
                <ConfirmationModal
                    isOpen={isModalOpen}
                    onClose={() => { setIsModalOpen(false); setIsBooking(false); }}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    initiatePayment={initiatePayment}
                    isBooking={isBooking}
                    handleApplyCoupon={handleApplyCoupon}
                    handleRemoveCoupon={handleRemoveCoupon}
                    appliedCoupon={appliedCoupon}
                    couponError={couponError}
                    couponLoading={couponLoading}
                    priceDetails={priceDetails}
                />
            )}
        </Wrapper>
    );
};

export default Counselling;
